import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useSignedMediaUrl } from "@/lib/media/signedMediaUrl";

interface PreviewAsset {
  id: string;
  file_name: string;
  file_url: string;
}

interface Props {
  asset: PreviewAsset | null;
  assets?: PreviewAsset[];
  onClose: () => void;
  onNavigate?: (asset: PreviewAsset) => void;
}

export default function InfographicPreviewModal({ asset, assets = [], onClose, onNavigate }: Props) {
  const signedUrl = useSignedMediaUrl(asset?.file_url);
  if (!asset) return null;

  const currentIndex = assets.findIndex(a => a.id === asset.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < assets.length - 1;

  const goPrev = () => {
    if (hasPrev && onNavigate) onNavigate(assets[currentIndex - 1]);
  };
  const goNext = () => {
    if (hasNext && onNavigate) onNavigate(assets[currentIndex + 1]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") goPrev();
    else if (e.key === "ArrowRight") goNext();
    else if (e.key === "Escape") onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      ref={(el) => el?.focus()}
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-card/80 border border-border/50 flex items-center justify-center hover:bg-card transition-colors"
      >
        <X className="w-5 h-5 text-foreground" />
      </button>

      {/* Prev arrow */}
      {hasPrev && onNavigate && (
        <button
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-card/80 border border-border/50 flex items-center justify-center hover:bg-card transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-foreground" />
        </button>
      )}

      {/* Next arrow */}
      {hasNext && onNavigate && (
        <button
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-card/80 border border-border/50 flex items-center justify-center hover:bg-card transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-foreground" />
        </button>
      )}

      {/* Image */}
      <div className="max-w-5xl w-full flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
        <img
          src={signedUrl ?? undefined}
          alt={asset.file_name}
          className="max-w-full max-h-[80vh] object-contain rounded-xl"
        />
        <p className="text-xs text-muted-foreground font-body">
          {asset.file_name}
          {currentIndex >= 0 && assets.length > 0 && (
            <span className="ml-2 text-foreground/50">
              ({currentIndex + 1} / {assets.length})
            </span>
          )}
        </p>
      </div>
    </div>
  );
}
